namespace e5 {
    export interface MultitouchOptions extends ActorOptions {
        minScale?: number;
        maxScale?: number;
        pan?: boolean;
        rotate?: boolean;
        pinch?: boolean;
        clamp?: ClampOptions;
        clampTrigger?: Trigger;
        snap?: SnapOptions;
        snapTrigger?: Trigger;
    }

    export class Multitouch extends Actor<Pointer, MultitouchOptions> implements MultitouchOptions {
        public static DEFAULT: MultitouchOptions = {
            minScale: NaN,
            maxScale: NaN,
            rotate: true,
            pinch: true,
            pan: true,
            clamp: null,
            clampTrigger: Trigger.CLEAR,
            snap: null,
            snapTrigger: Trigger.CLEAR
        };

        public minScale: number;
        public maxScale: number;
        public pan: boolean;
        public rotate: boolean;
        public pinch: boolean;
        public clamp: ClampOptions;
        public clampTrigger: Trigger;
        public snap: SnapOptions;
        public snapTrigger: Trigger;

        private _onTrigger = e5.Signal.create<[Trigger]>();
        private _pinPoint: Point3d;
        private _center: Point = new Point();
        private _distance: number = 1;
        private _angle: number = 0;
        private _target: HTMLElement = null;
        private _transform: Transform = new Transform();

        constructor(target: HTMLElement = null, options: MultitouchOptions = null) {
            super(null, ActorNonOptions);

            this._transform.mode = TransformMode.Inject;

            if (options !== ActorNonOptions)
                this.setOptions(options);

            this.target = target;

            this.onDown.add(() => {
                this._onTrigger.dispatch(Trigger.START);
                this.refresh();
            });

            this.onMove.add(() => {
                this.update();
            });

            this.onUp.add(() => {
                if (this.count > 0)
                    this.refresh();
                else
                    this._onTrigger.dispatch(Trigger.CLEAR);
            });

            this._onTrigger.add((trigger) => {
                if (this.snap && (trigger & this.snapTrigger)) {
                    this.snap.useTransform = true;
                    Snap.apply(this.target, this.snap);
                }

                if (this.clamp && (trigger & this.clampTrigger)) {
                    this.clamp.transformMode = TransformMode.Inject;
                    this.clamp.useTransform = true;
                    Clamp.apply(this.target, this.clamp);
                }
            });
        }

        public setOptions(options: MultitouchOptions) {
            options = ObjectUtil.extend({}, Multitouch.DEFAULT, options);
            this.minScale = options.minScale;
            this.maxScale = options.maxScale;
            this.pan = options.pan;
            this.rotate = options.rotate;
            this.pinch = options.pinch;
            this.clamp = options.clamp;
            this.clampTrigger = options.clampTrigger;
            this.snap = options.snap;
            this.snapTrigger = options.snapTrigger;
            super.setOptions(options);
        }

        public get target(): HTMLElement {
            return this._target || this.element;
        }

        public set target(target: HTMLElement) {
            if (this._target === target)
                return;
            this._target = target;
            if (target && !this.element)
                this.element = target;
        }

        private update(): void {
            var target = this.target;
            if (!target)
                return;

            this._transform.update(target);

            var center = this.center;
            var local = this._transform.toLocal(center.x, center.y, 0);
            var matrix = this._transform.matrix;

            /**
             * Get the absolute tranform-origin.
             */
            var origin = this._transform.origin;

            /**
             * Panning
             */
            if (this.pan) {
                matrix.appendPosition(local.x - this._pinPoint.x - origin.x, local.y - this._pinPoint.y - origin.y, 0);
            }

            /**
             * Pinching/Scaling
             */
            if (this.pinch && this.count > 1) {
                var distance = this.distance;
                var scaleDelta = distance / this._distance;
                this._distance = distance;

                //Clamp scaling.
                if (!isNaN(this.maxScale) || !isNaN(this.minScale)) {
                    var scale = matrix.getScale();
                    var realScale = scale.x * scaleDelta;
                    if (!isNaN(this.maxScale))
                        realScale = Math.min(realScale, this.maxScale);
                    if (!isNaN(this.minScale))
                        realScale = Math.max(realScale, this.minScale);
                    //Avoid division by zero.
                    if (scale.x === 0)
                        scale.x = 0.000001;
                    scaleDelta = realScale / scale.x;
                }

                matrix.appendPosition({ x: local.x, y: local.y, z: 0, w: 1 });
                matrix.appendScale({ x: scaleDelta, y: scaleDelta, z: 1, w: 1 });
                matrix.appendPosition({ x: -local.x, y: -local.y, z: 0, w: 1 });
            }

            /**
             * Rotate
             */
            if (this.rotate && this.count > 1) {
                var angle = Actor.angle(this.pointers);
                var angleDelta = angle - this._angle;
                this._angle = angle;

                matrix.appendPosition({ x: local.x, y: local.y, z: 0, w: 1 });
                matrix.appendRotation({ x: 0, y: 0, z: angleDelta * 180 / Math.PI, w: 1 });
                matrix.appendPosition({ x: -local.x, y: -local.y, z: 0, w: 1 });
            }

            matrix.appendPosition(origin.x, origin.y, 0);

            target.style.transform = matrix.getCss3d();

            this._onTrigger.dispatch(Trigger.MOVE);
        }

        private refresh(): void {
            this._center.copyFrom(this.center);
            this._transform.update(this.target);
            this._pinPoint = this._transform.toLocal(this._center.x, this._center.y, 0);
            if (this.count > 1) {
                this._distance = Actor.distance(this.pointers);
                this._angle = Actor.angle(this.pointers);
            }
        }

        public dispose(): void {
            this._onTrigger.dispose();
            this._onTrigger = null;
            this.target = null;
            this.clamp = null;
            this._pinPoint = null;
            this._center = null;
            this._transform.dispose();
            this._transform = null;
            super.dispose();
        }
    }
}